/**
 * Move-in Service configuration modal (BV-configured, buyer-requested).
 *
 * Captures the source + destination addresses and the move-in date window for the MOVE_IN_SERVICE
 * package. BHK is auto-derived from the flat's config (read-only) and local-vs-intercity is derived
 * server-side from the two addresses. Once saved, the move-in SERVICE tender can be floated.
 */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MapPin, ArrowRight } from "lucide-react";
import { FormModal, Form, Field, FormSubmit, AddressFields } from "../../components/form";
import { getMoveInConfig, saveMoveInConfig } from "../../services/interiorExecution";

const ADDR_KEYS = ["buildingNo", "streetName1", "streetName2", "city", "district", "state", "pinCode", "country"];

const pickAddr = (a) => {
  const out = {};
  ADDR_KEYS.forEach((k) => { out[k] = a?.[k] ?? ""; });
  return out;
};

// Date <-> "YYYY-MM-DD" (local parts, no TZ shift).
const toDate = (s) => (s ? new Date(s) : null);
const toISO = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const MoveInConfigModal = ({ leadId, open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!open || !leadId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getMoveInConfig(leadId);
        if (!cancelled) setConfig(res || null);
      } catch (e) {
        if (!cancelled) setConfig(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, leadId]);

  const defaultValues = {
    sourceAddress: pickAddr(config?.sourceAddress),
    destinationAddress: pickAddr(config?.destinationAddress),
    moveInDateStart: toDate(config?.moveInDateStart),
    moveInDateEnd: toDate(config?.moveInDateEnd),
    notes: config?.notes || "",
  };

  const handleSubmit = async (values) => {
    try {
      const body = {
        sourceAddress: values.sourceAddress,
        destinationAddress: values.destinationAddress,
        moveInDateStart: toISO(values.moveInDateStart),
        moveInDateEnd: toISO(values.moveInDateEnd),
        notes: values.notes || null,
      };
      const saved = await saveMoveInConfig(leadId, body);
      setConfig(saved || null);
      toast.success("Move-in details saved");
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save move-in details");
    }
  };

  return (
    <FormModal open={open} onClose={onClose} title="Configure Move-in Service" size="lg">
      {loading ? (
        <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
      ) : (
        <>
          {/* Derived, read-only context */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
              BHK: {config?.bhk || "—"} <span className="text-slate-400">(from flat config)</span>
            </span>
            {config && (
              <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-semibold ${config.isIntercity ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                {config.isIntercity ? "Intercity move" : "Local move"}
              </span>
            )}
          </div>

          <Form defaultValues={defaultValues} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                  <MapPin size={14} className="text-flamingo-600" /> Source address
                </p>
                <AddressFields prefix="sourceAddress" required={["city", "state", "pinCode"]} />
              </div>
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                  <ArrowRight size={14} className="text-flamingo-600" /> Destination address
                </p>
                <AddressFields prefix="destinationAddress" required={["city", "state", "pinCode"]} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field name="moveInDateStart" label="Move-in from" type="date" placeholder="Start date" />
              <Field name="moveInDateEnd" label="Move-in to" type="date" placeholder="End date" />
            </div>

            <Field name="notes" label="Notes (optional)" type="textarea" placeholder="Anything the movers should know" />

            <FormSubmit>Save move-in details</FormSubmit>
          </Form>
        </>
      )}
    </FormModal>
  );
};

export default MoveInConfigModal;
